import { useState } from "react";
import { Mail, Pause, Play, RefreshCw, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAgentMailboxes, useDeprovisionAgentMailbox, useUpdateAgentMailbox } from "./hooks/useAgentMailboxes";

import type { AgentMailbox, AgentMailboxStatus } from "@/types/agent-mailboxes";
const STATUS_LABELS: Record<AgentMailboxStatus, string> = {
  pending: "Pending",
  provisioned: "Active",
  suspended: "Suspended",
  failed: "Failed",
  unavailable: "Unavailable",
};

const getStatusBadge = (status: AgentMailboxStatus) => {
  switch (status) {
    case "provisioned":
      return <Badge className="bg-genie-primary/10 text-genie-primary border-genie-primary/20">Active</Badge>;
    case "suspended":
      return (
        <Badge variant="outline" className="text-genie-accent border-genie-accent/40">
          Suspended
        </Badge>
      );
    case "pending":
      return <Badge variant="secondary">Pending</Badge>;
    case "failed":
      return <Badge variant="destructive">Failed</Badge>;
    case "unavailable":
      return (
        <Badge variant="outline" className="text-genie-text-muted">
          Unavailable
        </Badge>
      );
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
};

const PAGE_SIZE = 50;

const AgentMailboxesPage = () => {
  const [page, setPage] = useState(1);
  const { data, isLoading, refetch } = useAgentMailboxes(page);
  const updateMailbox = useUpdateAgentMailbox();
  const deprovisionMailbox = useDeprovisionAgentMailbox();

  const mailboxes = data?.mailboxes;
  const totalCount = data?.count ?? 0;

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [pendingDeprovision, setPendingDeprovision] = useState<AgentMailbox | null>(null);

  const filtered = (mailboxes ?? []).filter((mailbox) => {
    if (statusFilter !== "all" && mailbox.status !== statusFilter) return false;
    if (!search.trim()) return true;
    const needle = search.trim().toLowerCase();
    return (
      mailbox.email_address?.toLowerCase().includes(needle) ||
      mailbox.agent_name?.toLowerCase().includes(needle) ||
      mailbox.user_email?.toLowerCase().includes(needle) ||
      mailbox.user_name?.toLowerCase().includes(needle)
    );
  });

  const handleCopyAddress = async (email: string) => {
    try {
      await navigator.clipboard.writeText(email);
      toast.success("Mailbox address copied to clipboard");
    } catch {
      toast.error("Failed to copy");
    }
  };

  const handleToggleSuspend = async (mailbox: AgentMailbox) => {
    const action = mailbox.status === "suspended" ? "resume" : "suspend";
    try {
      await updateMailbox.mutateAsync({ id: mailbox.id, input: { action } });
      toast.success(action === "suspend" ? "Mailbox suspended" : "Mailbox resumed");
    } catch (error: any) {
      toast.error(`Failed to ${action} mailbox: ${error.message}`);
    }
  };

  const handleDeprovision = async () => {
    if (!pendingDeprovision) return;
    try {
      await deprovisionMailbox.mutateAsync(pendingDeprovision.id);
      toast.success("Mailbox deprovisioned");
      setPendingDeprovision(null);
    } catch (error: any) {
      toast.error(`Failed to deprovision mailbox: ${error.message}`);
    }
  };

  return (
    <div className="min-h-screen bg-genie-background p-4 md:p-8">
      <div className="max-w-[1920px] mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Mail className="h-8 w-8 text-genie-secondary" />
            <div>
              <h1 className="text-3xl font-bold text-genie-text-primary">Agent Mailboxes</h1>
              <p className="text-genie-text-secondary">Manage Genie agent email inboxes provisioned via HG Mail</p>
            </div>
          </div>
          <Button onClick={() => refetch()} variant="outline" className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>

        <div className="bg-white rounded-lg p-4 mb-6 border border-genie-border">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative md:col-span-2">
              <Search className="absolute left-3 top-3 h-4 w-4 text-genie-text-muted" />
              <Input
                placeholder="Filter this page by email, genie, or user..."
                className="pl-10"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {Object.entries(STATUS_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-genie-border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email Address</TableHead>
                <TableHead>Genie</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="hidden lg:table-cell">Last Error</TableHead>
                <TableHead className="hidden xl:table-cell">Updated</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-genie-text-secondary">
                    <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2 text-genie-primary" />
                    Loading mailboxes...
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-genie-text-muted">
                    No agent mailboxes found
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((mailbox) => {
                  const canToggle = mailbox.status === "provisioned" || mailbox.status === "suspended";
                  const canDeprovision = mailbox.status !== "unavailable";
                  const isSuspended = mailbox.status === "suspended";
                  return (
                    <TableRow key={mailbox.id}>
                      <TableCell className="font-mono text-sm text-genie-text-primary">
                        <CopyableEmail email={mailbox.email_address} />
                      </TableCell>
                      <TableCell className="text-genie-text-primary font-medium">{mailbox.agent_name ?? "—"}</TableCell>
                      <TableCell>
                        <div className="text-sm text-genie-text-primary">{mailbox.user_name ?? "—"}</div>
                        {mailbox.user_email && (
                          <CopyableEmail email={mailbox.user_email} className="text-xs text-genie-text-secondary" />
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          {getStatusBadge(mailbox.status)}
                          {mailbox.test_mode && (
                            <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50">
                              Test
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-xs text-genie-text-secondary max-w-xs truncate">
                        {mailbox.last_error ?? "—"}
                      </TableCell>
                      <TableCell className="hidden xl:table-cell text-sm text-genie-text-secondary">
                        {new Date(mailbox.updated_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={!canToggle || updateMailbox.isPending}
                            onClick={() => handleToggleSuspend(mailbox)}
                          >
                            {isSuspended ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-destructive hover:text-destructive"
                            disabled={!canDeprovision}
                            onClick={() => setPendingDeprovision(mailbox)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>

          {totalCount > PAGE_SIZE && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t border-genie-border text-sm">
              <div className="text-genie-text-secondary order-2 sm:order-1">
                Showing {(page - 1) * PAGE_SIZE + 1} to {Math.min(page * PAGE_SIZE, totalCount)} of {totalCount}{" "}
                mailboxes
              </div>
              <div className="flex gap-2 order-1 sm:order-2 w-full sm:w-auto">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => p - 1)}
                  disabled={page === 1}
                  className="flex-1 sm:flex-none"
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page * PAGE_SIZE >= totalCount}
                  className="flex-1 sm:flex-none"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <AlertDialog open={!!pendingDeprovision} onOpenChange={(open) => !open && setPendingDeprovision(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deprovision this mailbox?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes the Nylas grant for{" "}
              <CopyableEmail email={pendingDeprovision?.email_address} className="font-medium" /> in HG Mail. The Genie
              will stop receiving email until a new mailbox is provisioned. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDeprovision}
              disabled={deprovisionMailbox.isPending}
            >
              Deprovision
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AgentMailboxesPage;
